import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE, getImageUrl } from '../../utils/api'

interface Reel {
    _id: string;
    title: string;
    videoUrl: string;
    thumbnail: string;
    location: string;
}

export default function AdminManageReels() {
    const { token } = useAuth()
    const [reels, setReels] = useState<Reel[]>([])
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [showForm, setShowForm] = useState(false)

    const [form, setForm] = useState({
        title: '',
        videoUrl: '',
        thumbnail: '',
        location: ''
    })

    useEffect(() => {
        fetchReels()
    }, [])

    const fetchReels = async () => {
        try {
            const res = await fetch(`${API_BASE}/reels`)
            const data = await res.json()
            if (Array.isArray(data)) setReels(data)
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const extractYouTubeId = (url: string) => {
        const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([^"&?\/\s]{11})/;
        const match = url.match(regex);
        return match ? match[1] : null;
    };

    const handleVideoUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const url = e.target.value;
        const videoId = extractYouTubeId(url);
        
        if (videoId) {
            const thumbUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
            setForm({ ...form, videoUrl: url, thumbnail: thumbUrl });
        } else {
            setForm({ ...form, videoUrl: url });
        }
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!token) return
        setSubmitting(true)
        setMsg(null)

        try {
            const res = await fetch(`${API_BASE}/reels${editingId ? `/${editingId}` : ''}`, {
                method: editingId ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(form)
            })

            if (res.ok) {
                setMsg({ type: 'success', text: editingId ? 'Reel updated successfully!' : 'Reel added successfully!' })
                setForm({ title: '', videoUrl: '', thumbnail: '', location: '' })
                setEditingId(null)
                setShowForm(false)
                fetchReels()
            } else {
                const errData = await res.json()
                setMsg({ type: 'error', text: errData.message || `Failed to ${editingId ? 'update' : 'add'} reel` })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error. Please try again.' })
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = async (id: string) => {
        if (!token || !window.confirm('Are you sure you want to delete this reel?')) return
        try {
            const res = await fetch(`${API_BASE}/reels/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            })
            if (res.ok) {
                setReels(prev => prev.filter(r => r._id !== id))
                setMsg({ type: 'success', text: 'Reel deleted.' })
                if (editingId === id) {
                    setEditingId(null)
                    setForm({ title: '', videoUrl: '', thumbnail: '', location: '' })
                }
            }
        } catch (err) {
            console.error(err)
        }
    }

    const handleEdit = (r: Reel) => {
        setEditingId(r._id)
        setForm({
            title: r.title,
            videoUrl: r.videoUrl,
            thumbnail: r.thumbnail,
            location: r.location
        })
        setShowForm(true)
    }

    const cancelEdit = () => {
        setEditingId(null)
        setForm({ title: '', videoUrl: '', thumbnail: '', location: '' })
        setShowForm(false)
    }

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div>
                    <h1 className="text-3xl font-bold text-black mb-2 tracking-tight">Social Reels</h1>
                    <p className="text-black text-sm font-bold">Manage short-form vertical video content to drive high engagement.</p>
                </div>
                <button 
                    onClick={() => { setEditingId(null); setForm({ title: '', videoUrl: '', thumbnail: '', location: '' }); setShowForm(true); }}
                    className="px-8 py-4 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-md font-bold text-[14px] transition-all shadow-md active:scale-95 flex items-center gap-3"
                >
                    <i className="fa-solid fa-plus text-[12px]"></i> Publish New Reel
                </button>
            </div>

                {/* Notification Area */}
                {msg && (
                    <div className={`mb-10 p-5 rounded-md flex items-center gap-4 animate-in fade-in slide-in-from-top-4 border shadow-sm ${
                        msg.type === 'error' 
                        ? 'bg-rose-50 text-rose-700 border-rose-100' 
                        : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                    }`}>
                        <i className={`fa-solid ${msg.type === 'error' ? 'fa-triangle-exclamation' : 'fa-circle-check'} text-lg`}></i>
                        <p className="text-[14px] font-bold">{msg.text}</p>
                    </div>
                )}

            <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-2xl shadow-slate-200/50 dark:shadow-none overflow-hidden mb-12">
                <div className="p-8 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between bg-white/50 dark:bg-slate-800/30">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-md bg-white border-2 border-[#05144b] text-[#05144b] flex items-center justify-center shadow-xl shadow-blue-100">
                            <i className="fa-solid fa-clapperboard text-lg"></i>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-black tracking-tight">Video Galleries</h3>
                            <p className="text-[12px] font-bold text-black/40 uppercase tracking-widest">{reels.length} Active Vertical Shorts</p>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="p-32 text-center">
                            <div className="w-16 h-16 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                            <p className="text-black font-bold text-[14px]">Fetching Visuals...</p>
                        </div>
                    ) : (
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-white/50 dark:bg-slate-800/30 border-b border-blue-900/10 dark:border-slate-800">
                                    <th className="px-8 py-5 text-[12px] font-bold text-black uppercase">Preview & Concept</th>
                                    <th className="px-8 py-5 text-[12px] font-bold text-black uppercase text-right">Operations</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {reels.map(r => (
                                    <tr key={r._id} className="group hover:bg-white/50 dark:hover:bg-slate-800/20 transition-all">
                                        <td className="px-8 py-7">
                                            <div className="flex items-center gap-8">
                                                <div className="relative flex-shrink-0 group/thumb">
                                                    <div className="w-24 aspect-[9/16] rounded-md overflow-hidden ring-4 ring-white dark:ring-slate-800 shadow-2xl group-hover/thumb:scale-[1.05] transition-transform duration-500">
                                                        <img src={getImageUrl(r.thumbnail || 'https://via.placeholder.com/300x533')} alt={r.title} className="w-full h-full object-cover" />
                                                        <div className="absolute inset-0 bg-blue-950/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity rounded-md flex items-center justify-center backdrop-blur-[2px]">
                                                            <i className="fa-solid fa-play text-white text-2xl"></i>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="max-w-xl">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h4 className="font-bold text-black group-hover:text-black/60 transition-colors text-[17px] tracking-tight leading-tight">{r.title}</h4>
                                                        {r.location && (
                                                            <span className="px-3 py-1 rounded-md bg-gray-50 text-[10px] font-bold uppercase text-black border border-gray-100 tracking-widest">
                                                                {r.location}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-[12px] font-bold text-black/40 uppercase tracking-widest truncate max-w-xs">{r.videoUrl.split('/').pop()}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-7">
                                            <div className="flex items-center justify-end gap-3 transition-all">
                                                    <button 
                                                        className="w-11 h-11 rounded-md bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 transition-all flex items-center justify-center shadow-sm font-bold text-[12px]"
                                                        onClick={() => handleEdit(r)}
                                                    >
                                                        <i className="fa-solid fa-pen-nib text-xs"></i>
                                                    </button>
                                                    <button 
                                                        className="w-11 h-11 rounded-md bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm border border-rose-100 font-bold text-[12px]"
                                                        onClick={() => handleDelete(r._id)}
                                                    >
                                                        <i className="fa-solid fa-trash-can text-xs"></i>
                                                    </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {reels.length === 0 && (
                                    <tr>
                                        <td colSpan={2} className="px-8 py-32 text-center text-balance overflow-hidden">
                                            <div className="w-24 h-24 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-8 shadow-inner border border-gray-100">
                                                <i className="fa-solid fa-clapperboard text-4xl text-gray-200"></i>
                                            </div>
                                            <h3 className="text-xl font-bold text-black mb-2 tracking-tight">No Reels Detected</h3>
                                            <p className="text-black text-sm max-w-xs mx-auto font-bold">Kickstart your visual engagement by adding your first vertical reel to the platform.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Premium Dialog Overlay */}
            {showForm && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4 overflow-hidden">
                    <div className="absolute inset-0 bg-blue-950/80 backdrop-blur-sm animate-in fade-in duration-300" onClick={cancelEdit}></div>
                    <div className="relative w-full max-w-xl bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-2xl animate-in zoom-in-95 duration-300">
                        <div className="relative h-28 bg-[#05144b] flex flex-col justify-end p-6">
                            <button 
                                onClick={cancelEdit}
                                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors flex items-center justify-center"
                            >
                                <i className="fa-solid fa-xmark text-sm"></i>
                            </button>
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-md bg-white dark:bg-slate-900 p-1 shadow-2xl">
                                    <div className="w-full h-full rounded-[4px] bg-white dark:bg-slate-800 flex items-center justify-center overflow-hidden">
                                        {form.thumbnail ? (
                                            <img src={getImageUrl(form.thumbnail)} alt="Preview" className="w-full h-full object-cover" />
                                        ) : (
                                            <i className="fa-solid fa-clapperboard text-xl text-[#05144b]"></i>
                                        )}
                                    </div>
                                </div>
                                <div className="mb-1">
                                    <h2 className="text-lg font-black text-white italic tracking-tight">{editingId ? 'Modify reel' : 'New creator session'}</h2>
                                    <p className="text-blue-200/60 text-[9px] font-black italic tracking-widest uppercase">Visual configuration</p>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 px-8">
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-[11px] font-bold text-black uppercase mb-1.5">Reel Narrative</label>
                                            <input 
                                                type="text" 
                                                className="w-full px-5 py-3 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] text-black transition-all placeholder:text-gray-300 font-bold text-[14px] shadow-sm" 
                                                placeholder="Reel Headline"
                                                value={form.title} 
                                                onChange={e => setForm({ ...form, title: e.target.value })} 
                                                required 
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-bold text-black uppercase mb-1.5">Location Tag</label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-black">
                                                    <i className="fa-solid fa-location-dot text-xs"></i>
                                                </div>
                                                <input 
                                                    type="text" 
                                                    className="w-full px-5 py-3 pl-10 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] text-black transition-all placeholder:text-gray-300 font-bold text-[14px] shadow-sm" 
                                                    placeholder="Location (e.g. Mumbai)"
                                                    value={form.location} 
                                                    onChange={e => setForm({ ...form, location: e.target.value })} 
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-[11px] font-bold text-black uppercase mb-1.5">YouTube Source</label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                    <i className="fa-brands fa-youtube text-rose-500"></i>
                                                </div>
                                                <input 
                                                    type="text" 
                                                    className="w-full px-5 py-3 pl-10 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] text-black transition-all placeholder:text-gray-300 font-bold text-[14px] shadow-sm" 
                                                    placeholder="Paste YouTube Link..."
                                                    value={form.videoUrl} 
                                                    onChange={handleVideoUrlChange} 
                                                    required 
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-bold text-black uppercase mb-1.5">Poster Identification</label>
                                            <input 
                                                type="text" 
                                                className="w-full px-5 py-3 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] text-black transition-all placeholder:text-gray-300 font-bold text-[14px] shadow-sm" 
                                                placeholder="Auto-captured thumbnail"
                                                value={form.thumbnail} 
                                                onChange={e => setForm({ ...form, thumbnail: e.target.value })} 
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                                <button 
                                    type="button"
                                    onClick={cancelEdit}
                                    className="px-6 py-3 rounded-md bg-white text-black font-bold text-[12px] hover:bg-gray-50 transition-all border border-gray-100"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className={`px-8 py-3 rounded-md font-bold text-[14px] transition-all transform active:scale-95 shadow-md flex items-center justify-center gap-2 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50`}
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <i className={`fa-solid ${editingId ? 'fa-floppy-disk' : 'fa-paper-plane'} text-[12px]`}></i>
                                            <span>{editingId ? 'Update Reel' : 'Publish Reel'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}


